import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Link,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  StackDivider,
  Text,
  VStack
} from '@chakra-ui/react'
import { MoreVertDots } from '@components/icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { DateTime } from 'luxon'
import React from 'react'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import type { DocumentReferenceDto } from '~/clinician-api/models'
import { PDFIcon } from '../../../icons'

const policyNameSchema = z.object({
  name: z.string().min(1, { message: 'Name is required' })
})

type PolicyNameSchema = z.infer<typeof policyNameSchema>

// Utility function to convert bytes to MB
const convertBytesToMB = (bytes: number): string => {
  const mb = bytes / (1024 * 1024)
  return mb.toFixed(2) + ' MB'
}

interface PolicyItemProps {
  isEditing: boolean
  documentReference: DocumentReferenceDto
  onSaveDocumentReferenceName: (
    documentReferenceId: string,
    name: string
  ) => void
  onStartEditing: (documentReferenceId: string | null) => void
  onDeleteDocumentReference: (documentReferenceId: string) => void
}

export const PolicyItem: React.FC<PolicyItemProps> = ({
  documentReference,
  isEditing,
  onSaveDocumentReferenceName,
  onStartEditing,
  onDeleteDocumentReference
}) => {
  const handleSaveDocumentReferenceName = (formData: PolicyNameSchema) => {
    onSaveDocumentReferenceName(documentReference.id, formData.name)
    onStartEditing(null)
  }

  const handleCancelEditing = () => {
    onStartEditing(null)
  }

  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      w="100%"
      p="12px"
      bg={isEditing ? '#F6F7FE' : 'transparent'}
    >
      <HStack spacing="small">
        <Box
          p="12px"
          bg="rgba(87, 89, 91, 0.04)"
          borderRadius="8px"
          alignSelf="flex-start"
        >
          <PDFIcon />
        </Box>
        <VStack alignItems="flex-start">
          {isEditing ? (
            <Box maxWidth="360px">
              <PolicyNameEditForm
                documentReference={documentReference}
                onSave={handleSaveDocumentReferenceName}
                onCancel={handleCancelEditing}
              />
            </Box>
          ) : (
            <Box>
              <Text fontWeight="bold">
                {documentReference.description || documentReference.fileName}
              </Text>
            </Box>
          )}
          <Box>
            <HStack
              divider={
                <StackDivider
                  borderRight="1px solid"
                  borderRightColor="pale_gray"
                />
              }
            >
              <Text fontSize="xsm">{documentReference.fileName}</Text>
              <HStack>
                <Text fontSize="xsm">
                  {convertBytesToMB(documentReference.bytes)}
                </Text>
                <Text fontSize="xsm">
                  v{' '}
                  {DateTime.fromISO(documentReference.createdAt).toFormat(
                    'yyyy-MM-dd'
                  )}
                </Text>
              </HStack>
            </HStack>
          </Box>
        </VStack>
      </HStack>
      <Box>
        <Menu>
          <MenuButton data-menu-button>
            <MoreVertDots width="16px" />
          </MenuButton>
          <MenuList>
            <MenuItem onClick={() => onStartEditing(documentReference.id)}>
              Edit name
            </MenuItem>
            <MenuItem
              as={Link}
              href={documentReference.url}
              target="_blank"
              rel="noopener noreferrer"
              _hover={{ textDecoration: 'none' }}
            >
              <Text>Preview</Text>
            </MenuItem>
            <MenuDivider borderColor="pale_gray" />
            <MenuItem
              onClick={() => onDeleteDocumentReference(documentReference.id)}
            >
              <Text color="severe">Delete</Text>
            </MenuItem>
          </MenuList>
        </Menu>
      </Box>
    </Flex>
  )
}

interface PolicyNameEditFormProps {
  documentReference: DocumentReferenceDto
  onSave: (formData: PolicyNameSchema) => void
  onCancel: () => void
}

const PolicyNameEditForm: React.FC<PolicyNameEditFormProps> = ({
  documentReference,
  onSave,
  onCancel
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<PolicyNameSchema>({
    defaultValues: {
      name: documentReference.description || documentReference.fileName
    },
    resolver: zodResolver(policyNameSchema)
  })

  const handleCancel = () => {
    reset()
    onCancel()
  }

  return (
    <form onSubmit={handleSubmit(onSave)}>
      <FormControl isInvalid={!!errors.name} mb="xsmall">
        <FormLabel htmlFor="policy-name" mb="xsmall" fontWeight="bold">
          Edit Policy Name
        </FormLabel>
        <Input
          id="policy-name"
          w="100%"
          bg="white"
          autoFocus
          {...register('name')}
        />
        {!errors.name && (
          <FormHelperText color="dark_gray" fontSize="12px" mt="xxsmall">
            Clients will see this name. Choose something clear and professional.
          </FormHelperText>
        )}
        {errors.name && (
          <FormErrorMessage color="severe" fontSize="12px" mt="xxsmall">
            {errors.name.message}
          </FormErrorMessage>
        )}
      </FormControl>
      <HStack>
        <Button ml="0px" type="submit" borderRadius="8px" size="sm">
          Save
        </Button>
        <Button
          type="button"
          onClick={handleCancel}
          borderRadius="8px"
          size="sm"
          variant="outline"
        >
          Cancel
        </Button>
      </HStack>
    </form>
  )
}
